/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
import { ActiveLine, IActiveLine } from '../../../app-data/models/ActiveLine';
import { ActiveLineObject } from '../../../common/fancy-firebase/objects/ActiveLineObject';
import { ActiveLineApi } from '../../../app-data/state/ActiveLineApi';
import { RenderType } from 'unitTestUtils/SplitDataMock';
@Injectable({ providedIn: 'root' })
export class ActiveLineService extends AbstractCanvasConnection<ActiveLine> {
	private _api: ActiveLineApi;
	private _activeLineObject: ActiveLineObject;
	// BEGIN PLACEHOLDER: Replace as part of TP-6871
	/**
	 * We get a reference to the entire planState and not only the scopeSoup because of loading order shenanigans
	 * @private
	 */
	private _planState: any;
	// END PLACEHOLDER
	/**
	 * Registers the service to receive active line data from a `FancyFirebaseObject`
	 * @param fancyObject PlanMilestone object representing Plan Milestone data
	 * @param planState PlanState object
	 * @param activeLineApi ActiveLineApi the API that handles write actions
	 */
	// BEGIN PLACEHOLDER: Replace as part of TP-6871
	public registerPlanData(fancyObject: ActiveLineObject, planState: any, activeLineApi: ActiveLineApi): void {
		this._activeLineObject = fancyObject;
		this._api = activeLineApi;
		// We get a reference to the entire planState and not only the scopeSoup because of loading order shenanigans
		// By the time we register plan data, the scopeSoup has not been initialized yet...
		// So...since this will be replaced with a new calendar, figured it would be easier to only suffer a little bit.
		this._planState = planState;
		// END PLACEHOLDER
		super.registerPlanData();
		// This service is providedIn root, so there is no shutdownSubject/ngOnDestroy to hook into
		// Instead, it has been added to the PlanComponent destroy list and will be cleared up once the parent component is destroyed
		this._subscription = fancyObject.object$.subscribe((data: ActiveLine) => this._changed$.next(data));
	}
	/**
	 * Makes the floating active line calendar appear
	 */
	public showCalendar(): void {
		// BEGIN PLACEHOLDER: Replace as part of TP-6852
		this._planState.scopeSoup.showFloatingActiveCal();
		// END PLACEHOLDER
	}

	/**
	 * Sets the new (local) active line date
	 * @param date
	 */
	public setLineDate(date: string): void {
		this.update({ activeLineDate: date });
	}
	/**
	 * Sets the new (local) active line X position
	 * @param date
	 */
	public setLineX(x: number): void {
		this.update({ activeLineX: x });
	}
	/**
	 * Persist current data to Firebase
	 * @param renderType
	 */
	public save(renderType: string): void {
		this._api.persistData(this._activeLineObject.syncObject, renderType);
	}

	/**
	 * Switch to Edit View
	 */
	public switchToEdit(): void {
		this._api.changeView(this._activeLineObject.syncObject, RenderType.Edit);
	}
	/**
	 * Switch to Database View
	 */
	public switchToDatabase(): void {
		this._api.changeView(this._activeLineObject.syncObject, RenderType.Database);
	}
	/**
	 * Cancel editing and move back to "database" view
	 */
	public cancelEdit(): void {
		this._api.abortView(this._activeLineObject.syncObject, RenderType.Edit);
	}
	/**
	 * Private helper method to help update the local data state.
	 * @param data Partial<ActiveLine> The data to update in the local state
	 * @private
	 */
	private update(data: Partial<IActiveLine>): void {
		const key: string = this._activeLineObject.syncObject.$id;
		this._api.localUpdate(key, data);
	}

	/**
	 * Destroy all the things
	 */
	public destroy(): void {
		super.destroy();
		this._api.destroy();
		this._api = null;
		this._activeLineObject = null;
	}
}
